import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "reactstrap";
import { change, formValueSelector } from "redux-form";
import { useAccessible } from "src/authentication/hooks/useAccessible";
import { MerchantAccessRight } from "src/merchants/enums/MerchantAccessRight";
import {
  MERCHANT_IS_CHAT,
  MERCHANT_IS_DISPATCH_EXTENDED,
  MERCHANT_IS_TWO_STEP_OPT_IN,
  MERCHANT_MONTHLY_VOLUME_EXPECTED,
  MERCHANT_MONTHLY_VOLUME_LIMIT,
  MERCHANT_PROVISIONING_TYPE,
} from "src/merchants/forms/MerchantForm";
import { MerchantFormEnableMms } from "src/merchants/forms/MerchantFormEnableMms";
import { MerchantFormLongNumberProvisioning } from "src/merchants/forms/MerchantFormLongNumberProvisioning";
import { MerchantFormShortCodeNumberProvisioning } from "src/merchants/forms/MerchantFormShortCodeNumberProvisioning";
import type { Merchant } from "src/merchants/objects/Merchant";
import { CheckboxInput } from "src/shared/forms/inputs/CheckboxInputOld";
import { NumberInput } from "src/shared/forms/inputs/NumberInput";
import { RadioInput } from "src/shared/forms/inputs/RadioInputOld";

export interface IMerchantFormNumberProvisioningProps {
  form: string;
  merchant: Merchant;
  countryCode: string;
}

export const MerchantFormNumberProvisioning = ({
  form,
  merchant,
  countryCode,
}: IMerchantFormNumberProvisioningProps) => {
  const canChangeNumberProvisioning = !merchant?.mobileVirtualNumber;

  const selector = formValueSelector(form);

  const provisioningType = useSelector((state) =>
    selector(state, MERCHANT_PROVISIONING_TYPE),
  );
  const isProvisioningEnabled = Boolean(provisioningType);

  const dispatch = useDispatch();
  useEffect(() => {
    if (!isProvisioningEnabled) {
      dispatch(change(form, MERCHANT_IS_CHAT, false));
      dispatch(change(form, MERCHANT_IS_TWO_STEP_OPT_IN, false));
    }
  }, [dispatch, form, isProvisioningEnabled]);

  const canViewEditDispatchExtended = useAccessible(
    MerchantAccessRight.VIEW_EDIT_DISPATCH_EXTENDED,
  );
  const canConfigureMonthlyVolumes = useAccessible(
    MerchantAccessRight.CONFIGURE_MONTHLY_VOLUMES,
  );

  return (
    <>
      <MerchantFormLongNumberProvisioning
        form={form}
        canEdit={canChangeNumberProvisioning}
        countryCode={countryCode}
      />
      <MerchantFormShortCodeNumberProvisioning
        form={form}
        canEdit={canChangeNumberProvisioning}
        countryCode={countryCode}
      />
      {canChangeNumberProvisioning && (
        <RadioInput
          inputID={MERCHANT_PROVISIONING_TYPE}
          label={
            <FormattedMessage id="merchants.form.numberProvisioning.disabled" />
          }
          value=""
        />
      )}

      <hr />
      <Row>
        <Col sm={12}>
          <Row className="mb-3">
            <Col sm={12}>
              <CheckboxInput
                inputID={MERCHANT_IS_CHAT}
                label={
                  <FormattedMessage id="merchants.form.numberProvisioning.isChat" />
                }
                disabled={!isProvisioningEnabled}
              />
              <CheckboxInput
                inputID={MERCHANT_IS_TWO_STEP_OPT_IN}
                label={
                  <FormattedMessage id="merchants.numberProvisioning.isTwoStepOptIn" />
                }
                disabled={!isProvisioningEnabled}
              />
              {canViewEditDispatchExtended && (
                <CheckboxInput
                  inputID={MERCHANT_IS_DISPATCH_EXTENDED}
                  label={<FormattedMessage id="merchants.isDispatchExtended" />}
                />
              )}
              {useAccessible(MerchantAccessRight.ENABLE_MMS) && (
                <MerchantFormEnableMms
                  form={form}
                  provisioningType={provisioningType}
                  merchant={merchant}
                />
              )}
              {canConfigureMonthlyVolumes && (
                <Row>
                  <Col sm={12} className="my-3">
                    <NumberInput
                      inputID={MERCHANT_MONTHLY_VOLUME_EXPECTED}
                      label={
                        <FormattedMessage id="merchants.form.numberProvisioning.monthlyVolumeExpected" />
                      }
                      isInteger
                      isRequired
                      min={0}
                    />
                    <NumberInput
                      inputID={MERCHANT_MONTHLY_VOLUME_LIMIT}
                      label={
                        <FormattedMessage id="merchants.form.numberProvisioning.monthlyVolumeLimit" />
                      }
                      isInteger
                      isRequired
                      min={0}
                    />
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};
